
import { useState, useEffect } from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { Menu, X, User, LogOut, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useLanguage } from '@/context/LanguageContext';
import { isNativeApp } from '@/utils/platformUtils';
import { getLocalizedPath } from '@/utils/languageUtils';
import { useAuth } from '@/context/AuthContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import LanguageSwitcher from '../general/LanguageSwitcher';

export const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const { t, language } = useLanguage();
  const { lang } = useParams<{ lang?: string }>();
  const { isAuthenticated, user, signOut, isSessionValid, userRole } = useAuth();

  // Check if current route is a protected page that needs light navbar
  const isProtectedPage = false;
  //const isProtectedPage = /^\/(profile|dashboard|admin-dashboard|error|my-community|account-balance)$/.test(location.pathname) ||
  //  /^\/[a-z]{2}\/(profile|dashboard|admin-dashboard|error|my-products|my-community|account-balance)$/.test(location.pathname);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const navLinks = [
    { name: t('nav.home'), path: getLocalizedPath('/', language) },
    { name: t('nav.join'), path: getLocalizedPath('/sign-up', language) },
    { name: t('nav.tutorials'), path: getLocalizedPath('/tutorials', language) },
    { name: t('nav.strategies'), path: getLocalizedPath('/strategies', language) },
    { name: t('nav.referralOpportunity'), path: getLocalizedPath('/referral-program', language) },
    { name: t('nav.radex'), path: getLocalizedPath('/radex', language) },
  ];

  const signInText = t('backoffice.navbar.signIn');

  // Determine navbar styling based on page type and scroll state
  const native = isNativeApp();

  const getNavbarStyles = () => {
    if (isProtectedPage) {
      return {
        background: 'bg-white/95 backdrop-blur-lg shadow-lg',
        textColor: 'text-gray-900',
        linkColor: 'text-gray-700 hover:text-purple-600',
        logo: '/logo_1_no_background.png'
      };
    } else {
      if (native || isScrolled) {
        return {
          background: 'bg-gray-900/90 backdrop-blur-lg shadow-lg',
          textColor: 'text-white',
          linkColor: 'text-white/80 hover:text-purple-400',
          logo: '/logo_1_small_inverted_no_background.png'
        };
      } else {
        return {
          background: 'bg-transparent',
          textColor: 'text-white',
          linkColor: 'text-white/80 hover:text-purple-400',
          logo: '/logo_1_small_inverted_no_background.png'
        };
      }
    }
  };

  const navStyles = getNavbarStyles();

  return (
    <header
      className={cn(
        'fixed top-0 left-0 right-0 z-50 transition-all duration-300',
        navStyles.background,
        native ? 'pb-3' : (isProtectedPage ? 'py-3' : (isScrolled ? 'py-3' : 'py-5')),
        native && 'safe-area-top'
      )}
    >
      <div className="container mx-auto px-4">
        <nav className="flex items-center justify-between">
          <Link 
            to={getLocalizedPath('/', language)} 
            className="flex items-center py-1"
            aria-label="Home"
          >
            <img 
              src={navStyles.logo}
              alt="Company Logo" 
              className="h-10 w-auto my-2 sm:my-0" 
            />
          </Link>

          <div className="hidden md:flex items-center space-x-8">
            <div className="flex space-x-8">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={cn(
                    'font-medium text-base transition-all',
                    location.pathname === link.path
                      ? navStyles.linkColor
                      : navStyles.linkColor
                  )}
                >
                  {link.name}
                </Link>
              ))}
            </div>
            <div className="flex items-center space-x-3">
              <LanguageSwitcher />
              
              {isAuthenticated && isSessionValid() ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button 
                      className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium px-8 py-3 rounded-lg transition-all transform hover:scale-105 shadow-lg"
                      size="sm"
                    >
                      {user?.user_metadata?.first_name || user?.email?.split('@')[0] || 'Account'}
                      <ChevronDown className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent 
                    align="end" 
                    className={cn(
                      "w-48 border",
                      isProtectedPage 
                        ? "bg-white border-gray-200 text-gray-900" 
                        : "bg-gray-800 border-gray-700 text-white"
                    )}
                  >
                    {userRole === 'admin' ? (
                      <>
                        <DropdownMenuItem asChild className={cn(
                          "cursor-pointer",
                          isProtectedPage ? "hover:bg-gray-100 focus:bg-gray-100" : "hover:bg-gray-700 focus:bg-gray-700"
                        )}>
                          <Link to="/admin-dashboard" className="flex items-center cursor-pointer !text-white hover:!text-white">
                            <User className="mr-2 h-4 w-4" />
                            <span>{t('admin.dashboard.title')}</span>
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={signOut} 
                          className={cn(
                            "cursor-pointer !text-red-400 hover:!text-red-400",
                            isProtectedPage ? "hover:bg-gray-100 focus:bg-gray-100" : "hover:bg-gray-700 focus:bg-gray-700"
                          )}
                        >
                          <LogOut className="mr-2 h-4 w-4" />
                          <span>{t('auth.signOut')}</span>
                        </DropdownMenuItem>
                      </>
                    ) : (
                      <>
                        <DropdownMenuItem asChild className={cn(
                          "cursor-pointer",
                          isProtectedPage ? "hover:bg-gray-100 focus:bg-gray-100" : "hover:bg-gray-700 focus:bg-gray-700"
                        )}>
                          <Link to="/profile/the-club-user-profile" className="flex items-center cursor-pointer !text-white hover:!text-white">
                            <User className="mr-2 h-4 w-4" />
                            <span>{t('auth.profile')}</span>
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild className={cn(
                          "cursor-pointer",
                          isProtectedPage ? "hover:bg-gray-100 focus:bg-gray-100" : "hover:bg-gray-700 focus:bg-gray-700"
                        )}>
                          <Link to="/dashboard" className="flex items-center cursor-pointer !text-white hover:!text-white">
                            <User className="mr-2 h-4 w-4" />
                            <span>{t('common.dashboard')}</span>
                          </Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={signOut} 
                          className={cn(
                            "cursor-pointer !text-red-400 hover:!text-red-400",
                            isProtectedPage ? "hover:bg-gray-100 focus:bg-gray-100" : "hover:bg-gray-700 focus:bg-gray-700"
                          )}
                        >
                          <LogOut className="mr-2 h-4 w-4" />
                          <span>{t('auth.signOut')}</span>
                        </DropdownMenuItem>
                      </>
                    )}
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button 
                  className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white font-medium px-8 py-3 rounded-lg transition-all transform hover:scale-105 shadow-lg" 
                  size="sm"
                  asChild
                >
                  <Link to={getLocalizedPath('/sign-in', language)}>{signInText}</Link>
                </Button>
              )}
            </div>
          </div>

          {/* Mobile menu button */}
          <div className="md:hidden flex items-center gap-2">
            <LanguageSwitcher />
            <button
              type="button"
              className={navStyles.textColor}
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
            </button>
          </div>
        </nav>

        {/* Mobile menu */}
        {isMobileMenuOpen && (
          <div className={cn(
            "md:hidden absolute top-full left-0 right-0 backdrop-blur-lg shadow-lg animate-fade-in-up",
            isProtectedPage 
              ? "bg-white/95 border-t border-gray-200" 
              : "bg-gray-900/95 border-t border-gray-700"
          )}>
            <div className="flex flex-col py-4 px-4">
              {navLinks.map((link) => (
                <Link
                  key={link.path}
                  to={link.path}
                  className={cn(
                    'py-3 px-4 font-medium text-base border-b',
                    isProtectedPage 
                      ? 'border-gray-200 text-gray-700 hover:text-purple-600' 
                      : 'border-gray-800 text-white/80 hover:text-purple-400',
                    location.pathname === link.path
                      ? (isProtectedPage ? 'text-purple-600' : 'text-purple-400')
                      : ''
                  )}
                >
                  {link.name}
                </Link>
              ))}
              
              {isAuthenticated && isSessionValid() ? (
                <>
                  {userRole === 'admin' ? (
                    <>
                      <Link
                        to="/admin-dashboard"
                        className={cn(
                          "py-3 px-4 font-medium text-base border-b flex items-center",
                          isProtectedPage 
                            ? 'border-gray-200 text-gray-700' 
                            : 'border-gray-800 text-white/80'
                        )}
                      >
                        <User className="mr-2 h-4 w-4" />
                        <span>{t('admin.dashboard.title')}</span>
                      </Link>
                      <button
                        onClick={signOut}
                        className={cn(
                          "py-3 px-4 font-medium text-base border-b text-red-400 text-left flex items-center",
                          isProtectedPage ? 'border-gray-200' : 'border-gray-800'
                        )}
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        <span>{t('auth.signOut')}</span>
                      </button>
                    </>
                  ) : (
                    <>
                      <Link
                        to="/profile/the-club-user-profile"
                        className={cn(
                          "py-3 px-4 font-medium text-base border-b flex items-center",
                          isProtectedPage 
                            ? 'border-gray-200 text-gray-700' 
                            : 'border-gray-800 text-white/80'
                        )}
                      >
                        <User className="mr-2 h-4 w-4" />
                        <span>{t('auth.profile')}</span>
                      </Link>
                      <Link
                        to="/dashboard"
                        className={cn(
                          "py-3 px-4 font-medium text-base border-b flex items-center",
                          isProtectedPage 
                            ? 'border-gray-200 text-gray-700' 
                            : 'border-gray-800 text-white/80'
                        )}
                      >
                        <User className="mr-2 h-4 w-4" />
                        <span>{t('common.dashboard')}</span>
                      </Link>
                      <button
                        onClick={signOut}
                        className={cn(
                          "py-3 px-4 font-medium text-base border-b text-red-400 text-left flex items-center",
                          isProtectedPage ? 'border-gray-200' : 'border-gray-800'
                        )}
                      >
                        <LogOut className="mr-2 h-4 w-4" />
                        <span>{t('auth.signOut')}</span>
                      </button>
                    </>
                  )}
                </>
              ) : (
                <div className="py-4 px-4">
                  <Button className="w-full bg-purple-600 hover:bg-purple-700 text-white" asChild>
                    <Link to={getLocalizedPath('/sign-in', language)}>{signInText}</Link>
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
